import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  BarChart3,
  BellRing,
  BookOpenText,
  LayoutDashboard,
  LogOut,
  Settings,
  ShieldCheck,
  ShieldAlert,
  ShoppingBag,
  Trophy,
  UsersRound,
  Package,
  Tags,
  Layers,
  GraduationCap,
  ImagePlus,
  UserCog,
  User,
  Send,
} from 'lucide-react'
import { useAuth } from '@/hooks'

export function Sidebar() {
  const { 
    adminProfile, 
    signOutUser, 
    user, 
    isSuperAdmin,
    canViewUsers,
    canViewAnalytics,
    canEditContent,
    canEditProducts,
    canEditBanners,
    canViewSupport,
    canAccessSettings,
    canManageAdmins,
  } = useAuth()
  const location = useLocation();

  // Build nav items based on permissions
  const navItems = [
    { path: '/', label: 'Overview', icon: LayoutDashboard, show: true },
    { path: '/users', label: 'Users', icon: UsersRound, show: canViewUsers },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, show: canViewAnalytics },
    { 
      path: '/content', 
      label: 'Media & Konten', 
      icon: BookOpenText,
      show: canEditContent,
      subItems: canEditContent ? [
        { path: '/content?tab=articles', label: 'Artikel', icon: BookOpenText },
        { path: '/content?tab=classes', label: 'Kelas', icon: GraduationCap },
        { path: '/content?tab=categories', label: 'Kategori Konten', icon: Layers },
      ] : []
    },
    {
      path: '/commerce',
      label: 'Katalog Produk',
      icon: ShoppingBag,
      show: canEditProducts,
      subItems: canEditProducts ? [
        { path: '/commerce?tab=products', label: 'Daftar Produk', icon: Package },
        { path: '/commerce?tab=categories', label: 'Kategori Produk', icon: Tags },
      ] : []
    },
    { path: '/banners', label: 'Banner Manager', icon: ImagePlus, show: canEditBanners },
    // { path: '/translations', label: 'Languages', icon: Languages, show: canEditContent },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy, show: canViewAnalytics },
    { path: '/support', label: 'Support', icon: BellRing, show: canViewSupport },
    { path: '/push-notifications', label: 'Push Notifications', icon: Send, show: isSuperAdmin },
    { path: '/admin-management', label: 'Admin Access', icon: UserCog, show: canManageAdmins },
  ].filter(item => item.show)

  const bottomItems = [
    { path: '/profile', label: 'Profile', icon: User, show: true },
    { path: '/app-settings', label: 'Settings', icon: Settings, show: canAccessSettings },
  ].filter(item => item.show)

  return (
    <aside className="flex h-screen w-72 flex-col border-r border-stone-800 bg-[#141414] text-stone-300 shadow-2xl">
      {/* Logo */}
      <div className="border-b border-stone-800 p-6 flex items-center justify-center h-[90px]">
        <img alt="Parlent" className="h-12 w-auto object-contain" src="/parlent_logo_new.png" />
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 space-y-6 overflow-y-auto p-4 custom-scrollbar">
        <div className="space-y-1">
          <p className="px-4 pb-2 pt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">
            Workspace
          </p>
          {navItems.map((item) => {
            const isParentActive = location.pathname === item.path || (item.subItems && item.subItems.some(sub => {
              const [path, search] = sub.path.split('?');
              return location.pathname === path && location.search === `?${search}`;
            }));
            
            return (
              <div key={item.path}>
                <NavLink
                  to={item.path}
                  className={() =>
                    cn(
                      'flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-200',
                      isParentActive
                        ? 'bg-orange-600 text-white shadow-lg shadow-orange-900/50'
                        : 'text-stone-300 hover:bg-stone-800/80 hover:text-orange-400'
                    )
                  }
                >
                  <item.icon className={cn("h-5 w-5", "stroke-[2]")} />
                  {item.label}
                </NavLink>
                {item.subItems && item.subItems.length > 0 && isParentActive && (
                  <div className="mt-2 ml-4 pl-4 border-l border-stone-800 space-y-1">
                    {item.subItems.map((sub) => {
                      const [path, search] = sub.path.split('?');
                      const isSubActive = location.pathname === path && location.search === `?${search}`;
                      return (
                         <NavLink
                          key={sub.path}
                          to={sub.path}
                          className={cn(
                            'flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200',
                            isSubActive
                              ? 'bg-stone-800/80 text-orange-500'
                              : 'text-stone-400 hover:bg-stone-800/80 hover:text-orange-400'
                          )}
                        >
                          <sub.icon className="h-4 w-4" />
                          {sub.label}
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Admin Role Card */}
        <div className="mx-4 rounded-2xl border border-stone-800 bg-stone-900/50 p-4">
          <div className="mb-3 flex items-center gap-3">
            {isSuperAdmin ? (
              <ShieldAlert className="h-5 w-5 text-rose-500" />
            ) : (
              <ShieldCheck className="h-5 w-5 text-orange-500" />
            )}
            <div>
              <p className={cn(
                "text-sm font-semibold capitalize",
                isSuperAdmin ? "text-rose-400" : "text-stone-200"
              )}>
                {adminProfile?.role ?? 'viewer'}
                {isSuperAdmin && ' (Super)'}
              </p>
              <p className="text-xs text-stone-400 truncate w-32">{user?.email}</p>
            </div>
          </div>
          {isSuperAdmin && (
            <div className="text-[10px] text-rose-400/80 uppercase tracking-wider font-bold">
              Full Access
            </div>
          )}
        </div>
      </nav>

      {/* Bottom Navigation */}
      <div className="space-y-1 border-t border-stone-800 p-4 bg-[#141414]">
        {bottomItems.map((item) => (
          <NavLink
            key={`${item.path}-${item.label}`}
            to={item.path}
            className={() =>
              cn(
                'flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-all duration-200',
                location.pathname === item.path
                  ? 'bg-orange-600 text-white'
                  : 'text-stone-300 hover:bg-stone-800/80 hover:text-orange-400'
              )
            }
          >
            <item.icon className="h-5 w-5" />
            {item.label}
          </NavLink>
        ))}
        <button
          className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium text-rose-500 transition-all duration-200 hover:bg-stone-900 border border-transparent"
          onClick={() => void signOutUser()}
          type="button"
        >
          <LogOut className="h-5 w-5" />
          Sign Out
        </button>
      </div>
    </aside>
  )
}
